import { LockOutlined } from '@ant-design/icons'
import React from 'react'
import { useSelector } from 'react-redux'
import { Route, RouteProps } from 'react-router-dom'
import StatusMessage from '../statusMessage/StatusMessage'
import { getMe } from '../../store/organizationSlice'
import { OPEN_ROUTES, isRouteAllowed } from '../../utils/routePermissions'

/**
 * Rutt som bara renderas om tittaren har behörighet till den (#50).
 *
 * Backend nekar redan med 403, så det här är ingen sekretessgräns — det är skillnaden
 * mellan en tom sida och ett begripligt besked. Utan vakten renderades sidan, anropen
 * nekades, och användaren fick en skärm som såg trasig ut.
 *
 * Kartan rutt → behörighet ligger i utils/routePermissions.ts och nekar allt som inte
 * står med, så en ny rutt måste läggas till där för att bli nåbar.
 */
const GuardedRoute = ({ component: Component, path, ...rest }: RouteProps & { component: React.ComponentType<any> }) => {
  const me = useSelector(getMe)

  return (
    <Route
      {...rest}
      path={path}
      render={(props) => {
        const rutt = typeof path === 'string' ? path : ''

        // `me` hämtas asynkront av siderMenu och är tomt vid första renderingen
        // (id = 0 i initialState). Utan det här hade neka-som-förval blinkat till
        // med "Saknar behörighet" innan behörigheterna hunnit fram.
        //
        // Öppna rutter undantas: /app/callback är auth-flödets landningssida och
        // måste rendera direkt — den får inte vänta på ett anrop som i sin tur
        // förutsätter att inloggningen är klar.
        if (!OPEN_ROUTES.includes(rutt) && me.id === 0) return null

        if (!isRouteAllowed(rutt, me.permissions)) {
          return (
            <StatusMessage
              icon={<LockOutlined />}
              title="Du saknar behörighet till den här sidan"
              body="Vilka sidor du når styrs av din roll i Pejlen. Kontakta din administratör om du behöver se denna sida."
            />
          )
        }

        return <Component {...props} />
      }}
    />
  )
}

export default GuardedRoute
